import { NotFound } from "metabase/common/components/ErrorPages";
import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import { canAccessDataModel, useUserKeyValue } from "metabase/current-user";
import {
  PLUGIN_DATA_APPS,
  PLUGIN_DEPENDENCIES,
  PLUGIN_LIBRARY,
  PLUGIN_SCHEMA_VIEWER,
} from "metabase/plugins";
import { useSelector } from "metabase/redux";
import type { State } from "metabase/redux/store";
import {
  Navigate,
  Route,
  type RouteComponent,
  redirect,
  useIsNavigating,
} from "metabase/router";
import { getDataStudioTransformRoutes } from "metabase/transforms/routes";
import { canAccessTransforms } from "metabase/transforms/selectors";
import * as Urls from "metabase/urls";

import { getDataStudioActionRoutes } from "./actions/routes";
import { getDataStudioMetadataRoutes } from "./data-model/routes";
import { getDataStudioGlossaryRoutes } from "./glossary/routes";
import { GuidePage } from "./guide/pages/GuidePage/GuidePage";
import { CanAccessDataModel, CanAccessDataStudio } from "./route-guards";
import { getDataStudioSettingsRoutes } from "./settings/routes";
import { getDataStudioSnippetRoutes } from "./snippets/routes";

/**
 * The Data Studio layouts and upsell pages, in their own chunk. The route guards
 * stay eager: they have to decide before there is anything to show.
 */
const dataStudioLayout = () =>
  import(
    /* webpackChunkName: "data-studio" */ "./app/pages/DataStudioLayout"
  ).then(({ DataStudioLayout }) => ({
    Component: DataStudioLayout,
  }));

const dataSectionLayout = () =>
  import(
    /* webpackChunkName: "data-studio" */ "./app/pages/DataSectionLayout"
  ).then(({ DataSectionLayout }) => ({
    Component: DataSectionLayout,
  }));

const transformsSectionLayout = () =>
  import(
    /* webpackChunkName: "data-studio" */ "./app/pages/TransformsSectionLayout"
  ).then(({ TransformsSectionLayout }) => ({
    Component: TransformsSectionLayout,
  }));

const dependenciesSectionLayout = () =>
  import(
    /* webpackChunkName: "data-studio" */ "./app/pages/DependenciesSectionLayout"
  ).then(({ DependenciesSectionLayout }) => ({
    Component: DependenciesSectionLayout,
  }));

const gitSyncSectionLayout = () =>
  import(
    /* webpackChunkName: "data-studio" */ "./app/pages/GitSyncSectionLayout"
  ).then(({ GitSyncSectionLayout }) => ({ Component: GitSyncSectionLayout }));

const dependenciesUpsellPage = () =>
  import(
    /* webpackChunkName: "data-studio-upsells" */ "./upsells/pages/DependenciesUpsellPage"
  ).then(({ DependenciesUpsellPage }) => ({
    Component: DependenciesUpsellPage,
  }));

const libraryUpsellPage = () =>
  import(
    /* webpackChunkName: "data-studio-upsells" */ "./upsells/pages/LibraryUpsellPage"
  ).then(({ LibraryUpsellPage }) => ({
    Component: LibraryUpsellPage,
  }));

const schemaViewerUpsellPage = () =>
  import(
    /* webpackChunkName: "data-studio-upsells" */ "./upsells/pages/SchemaViewerUpsellPage"
  ).then(({ SchemaViewerUpsellPage }) => ({
    Component: SchemaViewerUpsellPage,
  }));

export function getDataStudioRoutes(IsAdmin: RouteComponent) {
  return (
    <>
      {/* These redirects sit
       * OUTSIDE the CanAccessDataStudio guard — users without Data Studio access must
       * still be forwarded —
       * and are declared BEFORE the guarded subtree so they win over its `path="*"`
       * catch-all
       */}

      {getDataStudioDependencyDiagnosticsRedirects()}
      {getDataStudioLibraryRedirects()}
      <Route element={<CanAccessDataStudio />}>
        <Route path="data-studio" lazy={dataStudioLayout}>
          <Route index element={<DataStudioIndexRedirect />} />
          <Route path="guide" element={<GuidePage />} />
          <Route path="data" element={<CanAccessDataModel />}>
            <Route lazy={dataSectionLayout}>
              {getDataStudioMetadataRoutes(IsAdmin)}
            </Route>
          </Route>
          <Route path="transforms" lazy={transformsSectionLayout}>
            {getDataStudioTransformRoutes()}
          </Route>
          {getDataStudioGlossaryRoutes()}
          {getDataStudioSnippetRoutes()}
          {getDataStudioActionRoutes(IsAdmin)}
          {PLUGIN_DATA_APPS.isEnabled &&
            PLUGIN_DATA_APPS.getDataStudioRoutes(IsAdmin)}
          {getDataStudioSettingsRoutes()}
          {PLUGIN_LIBRARY.isEnabled ? (
            PLUGIN_LIBRARY.getDataStudioLibraryRoutes(IsAdmin)
          ) : (
            <Route path="library" lazy={libraryUpsellPage} />
          )}
          {PLUGIN_LIBRARY.isEnabled ? (
            PLUGIN_LIBRARY.getDataStudioDashboardRoutes()
          ) : (
            <Route path="dashboards" lazy={libraryUpsellPage} />
          )}
          {PLUGIN_DEPENDENCIES.isEnabled ? (
            <Route path="dependencies" lazy={dependenciesSectionLayout}>
              {PLUGIN_DEPENDENCIES.getDataStudioDependencyRoutes()}
            </Route>
          ) : (
            <Route path="dependencies" lazy={dependenciesUpsellPage} />
          )}
          {PLUGIN_SCHEMA_VIEWER.isEnabled ? (
            <Route path="schema-viewer">
              {PLUGIN_SCHEMA_VIEWER.getDataStudioSchemaViewerRoutes()}
            </Route>
          ) : (
            <Route path="schema-viewer" lazy={schemaViewerUpsellPage} />
          )}
          <Route path="git-sync" lazy={gitSyncSectionLayout} />

          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </>
  );
}

/**
 * Dependency Diagnostics moved from Data Studio to Monitor.  */
export function getDataStudioDependencyDiagnosticsRedirects() {
  return (
    <>
      <Route
        path="data-studio/dependency-diagnostics"
        element={redirect(Urls.dependencyDiagnostics())}
      />
      <Route
        path="data-studio/dependency-diagnostics/*"
        element={redirect(`${Urls.dependencyDiagnostics()}/*`)}
      />
    </>
  );
}

/**
 * Snippets and data actions moved out of the library into their own sections.
 */
export function getDataStudioLibraryRedirects() {
  return (
    <>
      <Route
        path="data-studio/library/snippets/*"
        element={redirect(`${Urls.dataStudioSnippets()}/*`)}
      />
      <Route
        path="data-studio/library/actions/*"
        element={redirect(`${Urls.dataStudioActions()}/*`)}
      />
    </>
  );
}

export function DataStudioIndexRedirect() {
  const indexPath = useSelector(getIndexPath);
  const isNavigating = useIsNavigating();
  const { value: hasSeenGuide, isLoading } = useUserKeyValue({
    namespace: "data_studio",
    key: "hasSeenGuide",
    defaultValue: false,
  });

  if (isLoading || isNavigating) {
    return <LoadingAndErrorWrapper loading />;
  }

  return (
    <Navigate to={hasSeenGuide ? indexPath : Urls.dataStudioGuide()} replace />
  );
}

function getIndexPath(state: State) {
  if (canAccessDataModel(state)) {
    return Urls.dataStudioData();
  }
  if (canAccessTransforms(state)) {
    return Urls.transformList();
  }
  return Urls.dataStudioLibrary();
}
